import { useEffect, useRef, useState } from "react";
import { ExternalLink, RotateCcw } from "lucide-react";
import type { CrmForm } from "./types";
import type { PreviewScreen } from "../../pages/PublicForm";

/**
 * The form builder's live preview: the real public form in a frame beside the
 * builder, fed the UNSAVED draft as it changes, showing whichever screen is being
 * edited. It never saves answers or creates a lead (PublicForm's live mode).
 */
export default function FormPreview({ form, savedSlug, screen }: { form: CrmForm; savedSlug: string; screen: PreviewScreen }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);
  const [run, setRun] = useState(0);
  const latest = useRef({ form, screen });
  latest.current = { form, screen };

  const post = (message: Record<string, unknown>) => frame.current?.contentWindow?.postMessage(message, window.location.origin);
  const payload = (f: CrmForm) => ({ slug: f.slug, name: f.name, preview: true, questions: f.questions, settings: f.settings });

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.source !== frame.current?.contentWindow || e.data?.type !== "nla-preview-ready") return;
      const { form: f, screen: s } = latest.current;
      post({ type: "nla-preview-form", form: payload(f) });
      post({ type: "nla-preview-screen", screen: s, form: payload(f) });
      setReady(true);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Every edit reaches the preview as it is typed.
  useEffect(() => { if (ready) post({ type: "nla-preview-form", form: payload(form) }); }, [form, ready]);
  // A new screen object means the builder moved to another question or end screen.
  useEffect(() => { if (ready) post({ type: "nla-preview-screen", screen, form: payload(latest.current.form) }); }, [screen, ready]);

  return (
    <aside className="crm-preview" aria-label="Live preview">
      <div className="crm-preview-bar">
        <strong>Live preview</strong>
        <span className="crm-muted">Updates as you type</span>
        <span className="crm-preview-tools">
          <button type="button" className="icon-button" title="Start over" aria-label="Start the preview over" onClick={() => { setReady(false); setRun((n) => n + 1); }}><RotateCcw aria-hidden="true" /></button>
          <a className="icon-button" href={`/f/${encodeURIComponent(savedSlug)}?preview=1`} target="_blank" rel="noreferrer" title="Open the saved form in a new tab" aria-label="Open the saved form in a new tab"><ExternalLink aria-hidden="true" /></a>
        </span>
      </div>
      <iframe
        key={run}
        ref={frame}
        className={ready ? "is-ready" : ""}
        title="Live preview of this form"
        src={`/f/${encodeURIComponent(savedSlug)}?preview=1&live=1`}
      />
    </aside>
  );
}
